import React from 'react'
import PropTypes from 'prop-types'

import {
  requestIdleCallback,
  cancelIdleCallback,
} from '../../../../../core/helpers/polyfill'

import debounce from '../../../../../core/helpers/debounce'

function Profiler({
  id,
  delay = 3000,
  phase: phaseProp,
  onLog = () => {},
  ...rest
}) {
  const queue = React.useRef([])
  const taskHandle = React.useRef(null)
  const defer = React.useCallback(debounce(requestIdleCallback, delay), [delay])

  const enqueueReport = data => {
    queue.current.push(data)
    if (!taskHandle.current) {
      taskHandle.current = defer(runQueue)
    }
  }

  const runQueue = deadline => {
    while (
      (deadline.timeRemaining() > 0 || deadline.didTimeout) &&
      queue.current.length
    ) {
      const queueToSend = [...queue.current]
      queue.current = []
      onLog(queueToSend)
    }

    if (queue.current.length) {
      taskHandle.current = defer(runQueue)
    } else {
      taskHandle.current = 0
    }
  }

  React.useEffect(() => {
    return () => {
      taskHandle.current && cancelIdleCallback(taskHandle.current)
    }
  }, [])

  const handleReport = (
    id, // the "id" prop of the Profiler tree that has just committed
    phase, // either "mount" (if the tree just mounted) or "update" (if it re-rendered)
    actualDuration, // time spent rendering the committed update
    baseDuration, // estimated time to render the entire subtree without memoization
    startTime, // when React begins rendering this update
    commitTime, // when React has committed this update
    interactions, // the Set of interactions belonging to this update
  ) => {
    if (!phaseProp || phaseProp === phase) {
      enqueueReport({
        id,
        phase,
        actualDuration,
        baseDuration,
        startTime,
        commitTime,
        interactions: [...interactions],
      })
    }
  }

  return <React.Profiler id={id} onRender={handleReport} {...rest} />
}

Profiler.phases = {
  mount: 'mount',
  update: 'update',
}

Profiler.propTypes = {
  /**
   * The id prop of the Profiler tree that has just committed. This can be used to identify which part of the tree was committed if you are using multiple profilers.
   */
  id: PropTypes.string.isRequired,

  /**
   *  Identifies whether the tree has just been mounted for the first time or re-rendered due to a change in props, state, or hooks.
   */
  phase: PropTypes.oneOf(Object.values(Profiler.phases)),

  /**
   * Milliseconds delay interval for log the report. By default 3000ms
   * NOTE: The callback onLog will trigger only if the main thread is idle adding the {delay} milliseconds
   */
  delay: PropTypes.number,

  /**
   * Function to be called when the log is ready
   */
  onLog: PropTypes.func.isRequired,
}

export default Profiler

// This is an experimental code
export {
  unstable_trace as trace,
  unstable_wrap as wrap,
} from 'scheduler/tracing'
